import { AddIcon, DeleteIcon, QuestionIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, IconButton, Text, Tooltip, useToast } from '@chakra-ui/react';
import { PercentageNumberInput, TwNumberInput } from 'components/NumberInput';
import PropTypes from 'prop-types';
import { emptyRoasVectorRow, hasDuplicateRoasVectorMonths } from './roasVector';

const RoasVectorEditor = ({ points, onChange, isDisabled }) => {
  const toast = useToast();
  const rows = points?.length ? points : [emptyRoasVectorRow()];

  const updateRows = (nextRows) => {
    onChange(nextRows);
  };

  return (
    <Box gridColumn="1 / -1" borderLeft="3px solid" borderColor="purple.500" pl={4} mt={4}>
      <Flex align="center" gap={1} mb={3}>
        <Text fontWeight="semibold" fontSize="sm" color="purple.600">
          Expected ROAS vector
        </Text>
        <Tooltip
          label="Optional stored curve of expected cumulative ROAS by 0-indexed month. Not used in funding, repayment, or covenants yet."
          fontSize="xs"
          maxW="300px"
        >
          <QuestionIcon boxSize={3} color="gray.500" cursor="help" />
        </Tooltip>
      </Flex>
      <Box pb={4}>
        <Flex gap={4} mb={2} align="center">
          <Box w={120}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600">
              Month
            </Text>
          </Box>
          <Box w={150}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600">
              Cumulative ROAS %
            </Text>
          </Box>
          <Box w={10}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600">
              Action
            </Text>
          </Box>
        </Flex>
        {[...rows]
          .sort((a, b) => {
            if (a.month === '' || a.month === null || a.month === undefined) return 1;
            if (b.month === '' || b.month === null || b.month === undefined) return -1;
            return Number(a.month) - Number(b.month);
          })
          .map((point) => {
            const isDuplicate =
              rows.filter(
                (row) => row.id !== point.id && row.month !== '' && row.month === point.month
              ).length > 0;
            return (
              <Flex key={point.id} gap={4} mb={3} align="center">
                <Box w={120}>
                  <TwNumberInput
                    value={point.month}
                    min={0}
                    onChange={(value) => {
                      updateRows(
                        rows.map((row) => (row.id === point.id ? { ...row, month: value } : row))
                      );
                    }}
                    onBlur={() => {
                      if (isDuplicate && point.month !== '') {
                        toast({
                          title: 'Duplicate Month',
                          description: `Month ${point.month} is already used. Please choose a different month.`,
                          status: 'error',
                          duration: 4000,
                          isClosable: true,
                        });
                      }
                    }}
                    inputProps={{
                      fontWeight: 800,
                      borderColor: isDuplicate ? 'red.500' : undefined,
                    }}
                    isDisabled={isDisabled}
                  />
                  {isDuplicate && (
                    <Text fontSize="xs" color="red.500" mt={1} fontWeight="semibold">
                      Duplicate month - please fix before continuing
                    </Text>
                  )}
                </Box>
                <Box w={150}>
                  <PercentageNumberInput
                    value={point.cumulativeRoasPct}
                    onChange={(value) => {
                      updateRows(
                        rows.map((row) =>
                          row.id === point.id ? { ...row, cumulativeRoasPct: value } : row
                        )
                      );
                    }}
                    inputProps={{ fontWeight: 800 }}
                    isDisabled={isDisabled}
                  />
                </Box>
                {!isDisabled && (
                  <IconButton
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => {
                      updateRows(rows.filter((row) => row.id !== point.id));
                    }}
                    isDisabled={rows.length === 1}
                    aria-label="Delete ROAS vector month"
                  />
                )}
              </Flex>
            );
          })}
        {!isDisabled && (
          <Button
            leftIcon={<AddIcon />}
            size="sm"
            variant="outline"
            colorScheme="purple"
            onClick={() => {
              const newId = Math.max(...rows.map((row) => row.id)) + 1;
              updateRows([...rows, emptyRoasVectorRow(newId)]);
            }}
          >
            Add Month
          </Button>
        )}
      </Box>
    </Box>
  );
};

RoasVectorEditor.propTypes = {
  points: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

export const roasVectorHasDuplicates = hasDuplicateRoasVectorMonths;

export default RoasVectorEditor;
